import React, { FC, useRef, useMemo } from 'react'
import clsx from 'clsx'
import { useHeroAnimation } from 'src/components/sections/hero/hero-animations'
import { isInitialLoad } from 'src/helpers/animations'
import { ButtonSizes } from 'src/components/ui/Button'

import { Link } from 'gatsby-plugin-react-i18next'
import { useLocation } from '@reach/router'
import SectionHeader from 'src/components/ui/SectionHeader'
import FormattedHtmlContent from 'src/components/ui/FormattedHtmlContent'
import ButtonIcon from 'src/components/ui/ButtonIcon'
import { HighlightVariant } from 'src/components/sections/teaser/Highlight'
import MagneticScrollButton from 'src/components/ui/MagneticScrollButton'
import AuroraBackground from 'src/components/ui/AuroraBackground'
import IsoBadge from 'src/components/ui/IsoBadge'

export interface IHeroSectionProps {
  translationContent: {
    subtitle: string
    firstPartOfTitle: string
    secondPartOfTitle: string
    thirdPartOfTitle?: string
    content: string | React.ReactNode
    ctaLink: string
    ctaText: string
    ctaHoverText?: string
    cta2Text?: string
    cta2Link?: string
    cta2HoverText?: string
    cta2Size?: ButtonSizes
    sectionName: string
  }
  isH2?: boolean
  onButtonClick?: () => void
  isFullHeight?: boolean
  ctaSize?: ButtonSizes
  useFormattedHtml?: boolean
  contentClassName?: string
  sectionClassName?: string
  beforeTitleContent?: React.ReactNode
  showBackButton?: boolean
  backButtonTo?: string
  children?: React.ReactNode
  isFullWidthDescription?: boolean
  descriptionVariant?: HighlightVariant
  descriptionWidthClass?: string
  alignItems?: 'center' | 'start'
  disableBalance?: boolean
  showIso?: boolean
  showMagneticButton?: boolean
}

const HeroSection: FC<IHeroSectionProps> = ({
  translationContent: {
    subtitle,
    firstPartOfTitle,
    secondPartOfTitle,
    thirdPartOfTitle,
    content,
    ctaLink,
    ctaText,
    ctaHoverText,
    cta2Text,
    cta2Link,
    cta2HoverText,
    cta2Size,
    sectionName,
  },
  isH2,
  onButtonClick,
  isFullHeight,
  ctaSize = ButtonSizes.large,
  useFormattedHtml,
  contentClassName,
  sectionClassName,
  beforeTitleContent,
  showBackButton,
  backButtonTo,
  children,
  isFullWidthDescription,
  descriptionVariant = 'highlight',
  descriptionWidthClass = 'md:w-7/12',
  alignItems = 'center',
  disableBalance = true,
  showIso = true,
  showMagneticButton = true,
}) => {
  const subtitleRef = useRef<HTMLInputElement>(null)
  const pageTitleRef = useRef<HTMLInputElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const buttonWrapperRef = useRef<HTMLInputElement>(null)
  const badgesRef = useRef<HTMLDivElement>(null)
  const backButtonRef = useRef<HTMLDivElement>(null)
  const childrenRef = useRef<HTMLDivElement>(null)

  const location = useLocation()
  const pathname = location?.pathname || ''
  const isIsoPage = pathname.includes('/iso')
  const isSlides = pathname.includes('/slides')
  const isContact = pathname.includes('/contact')
  const isSecurity = pathname.includes('/security')
  const isTechnology = pathname.includes('/technology')
  const isPrivacyPolicy = pathname.includes('/privacy-policy')
  const shouldShowIso =
    showIso &&
    !isIsoPage &&
    !isSlides &&
    !isContact &&
    !isSecurity &&
    !isTechnology &&
    !isPrivacyPolicy

  const hasMagneticButton = showMagneticButton && Boolean(ctaText || ctaLink)

  const shouldAnimateWidgets = useMemo(() => isInitialLoad(), [])

  useHeroAnimation(
    subtitleRef,
    pageTitleRef,
    contentRef,
    buttonWrapperRef,
    badgesRef,
    backButtonRef,
    childrenRef
  )

  const hasBackButton = Boolean(showBackButton || beforeTitleContent)

  return (
    <div className={clsx('relative w-full overflow-visible', hasBackButton && 'md:pt-colossal')}>
      <section
        data-name={sectionName}
        id={sectionName}
        className={clsx(
          'container flex flex-col relative overflow-visible isolate bg-transparent h-fit',
          isFullHeight
            ? 'md:min-h-screen pt-large pb-semi-large md:pt-colossal md:pb-large xl:pb-extra-large'
            : 'pt-base pb-semi-large md:pt-colossal md:pb-large xl:pb-extra-large',
          hasBackButton && 'md:mt-colossal',
          sectionClassName
        )}
      >
        <AuroraBackground />
        <div
          className={clsx(
            'flex w-full flex-1 relative z-10 my-0 md:my-auto gap-8 md:gap-12',
            children
              ? clsx(
                  'flex-col lg:flex-row lg:justify-between',
                  alignItems === 'start' ? 'lg:items-start' : 'lg:items-center'
                )
              : 'flex-col justify-start md:justify-center'
          )}
        >
          <div
            className={clsx(
              'relative w-full',
              children && 'lg:w-1/2 lg:pr-10',
              !isH2 && 'md:mt-0'
            )}
          >
            {(showBackButton || beforeTitleContent) && (
              <div ref={backButtonRef} className="hidden md:block mb-8 md:mb-12 opacity-0">
                {showBackButton ? (
                  <Link to={backButtonTo} className="inline-block">
                    <ButtonIcon
                      size={ButtonSizes.large}
                      morphingIcon="arrow-to-x"
                    />
                  </Link>
                ) : (
                  beforeTitleContent
                )}
              </div>
            )}
            <div className="flex flex-col gap-base md:gap-semi-large xl:gap-extra-large w-full">
              <SectionHeader.Title
                subtitle={subtitle}
                titlePart1={firstPartOfTitle}
                titlePart2={secondPartOfTitle}
                titlePart3={thirdPartOfTitle}
                isH1={!isH2}
                subtitleRef={subtitleRef}
                titleRef={pageTitleRef}
              />

              {!useFormattedHtml && content && (
                <SectionHeader.Description
                  description={content}
                  descriptionVariant={descriptionVariant}
                  isFullWidthDescription={isFullWidthDescription}
                  descriptionWidthClass={descriptionWidthClass}
                  disableBalance={disableBalance}
                  contentRef={contentRef}
                />
              )}

              {shouldShowIso && !useFormattedHtml && (
                <div className="md:hidden flex flex-col gap-large w-full py-base">
                  <div className="w-full h-constant-1 bg-outline-base shrink-0" />
                  <div className="flex justify-end w-full">
                    <IsoBadge layout="text-above" align="right" />
                  </div>
                </div>
              )}

              {!hasMagneticButton && (ctaText || cta2Text) && (
                <SectionHeader.Actions
                  ctaText={ctaText}
                  ctaLink={ctaLink}
                  ctaHoverText={ctaHoverText}
                  ctaSize={ctaSize}
                  cta2Text={cta2Text}
                  cta2Link={cta2Link}
                  cta2HoverText={cta2HoverText}
                  cta2Size={cta2Size}
                  ctaRef={buttonWrapperRef}
                />
              )}
            </div>

            {useFormattedHtml && content && (
              <>
                <FormattedHtmlContent
                  ref={contentRef}
                  className={clsx(
                    'max-w-3xl w-full text-left text-megaDarkGray dark:text-white font-semibold',
                    contentClassName
                  )}
                  dangerouslySetInnerHTML={{ __html: content as string }}
                />
                {shouldShowIso && (
                  <div className="md:hidden flex flex-col gap-large w-full mt-large">
                    <div className="w-full h-constant-1 bg-outline-base shrink-0" />
                    <div className="flex justify-end w-full">
                      <IsoBadge layout="text-above" align="right" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {children && (
            <div ref={childrenRef} className="w-full lg:w-1/2 lg:pl-10 opacity-0">
              {children}
            </div>
          )}
        </div>
      </section>

      {hasMagneticButton && (
        <div
          ref={badgesRef}
          style={{ right: 'var(--grid-margin, 32px)' }}
          className={clsx(
            shouldAnimateWidgets ? 'opacity-0' : 'opacity-100',
            'hidden md:flex md:mt-0 md:absolute md:bottom-8 xl:bottom-12 pointer-events-none z-30 justify-end'
          )}
        >
          <div className="pointer-events-auto flex items-center justify-end">
            <MagneticScrollButton
              text={ctaText || 'explore our work'}
              targetId={ctaLink}
              onClick={onButtonClick}
            />
          </div>
        </div>
      )}
    </div>
  )
}

HeroSection.defaultProps = {
  onButtonClick: null,
  isFullHeight: true,
  isH2: false,
  useFormattedHtml: false,
  contentClassName: 'mt-12',
  sectionClassName: '',
  beforeTitleContent: null,
  showBackButton: false,
  backButtonTo: '/',
  isFullWidthDescription: false,
  descriptionWidthClass: 'md:w-7/12',
  disableBalance: true,
  showIso: true,
}

export default HeroSection
